/**
 * Content read as rules.
 *
 * Every number and every transition the cooking loop needs comes from
 * `recipes.json` and `timings.json`. This module is the only
 * place that reads them, so "how long is a chop", "what can go in the pan" and
 * "how long before a burnt pan sets the stove alight" each have exactly one
 * answer, and tuning any of them is a file edit.
 */

import type { Container, ContainerKind, IngredientId, StationType } from '@overcooked/shared'
import { msToTicks } from '@overcooked/shared'
import type { Content, ContainerDef, CookableDef, TransformDef } from './content'

/** The declared container kinds, in a stable order. */
export function containerDef(content: Content, kind: ContainerKind): ContainerDef | undefined {
  return content.recipes.containers[kind]
}

/**
 * How many ingredients a container holds. The pan holds one, which is what
 * makes a three-ingredient dish three legible cooks instead of one opaque
 * batch — capacity is a recipe fact, not an engine constant, and a kind nobody
 * declared falls back to one rather than throwing.
 */
export function containerCapacity(content: Content, kind: ContainerKind): number {
  const def = containerDef(content, kind)
  return def ? def.capacity : 1
}

/**
 * How many cooked items one plate holds.
 *
 * The counterpart to `containerCapacity`: the pan cooks one thing at a time, so
 * the plate is where a dish is assembled, and an unbounded plate is the next
 * soft-lock — pile the wrong four things onto it and only the bin gets them
 * off. Shipped as the longest dish's length, so every dish still fits and
 * nothing else does. Like `platesPerMap` it is a count rather than a duration,
 * so it is read here rather than through `durationTicks`.
 */
export function plateCapacity(content: Content): number {
  const value = content.timings.plateCapacity
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 1) {
    throw new Error('timings.json has no usable plateCapacity')
  }
  return Math.floor(value)
}

/** Whether this container may be set down on this static equipment. */
export function containerFitsEquipment(
  content: Content,
  kind: ContainerKind,
  station: StationType,
): boolean {
  const def = containerDef(content, kind)
  return def ? def.equipment.includes(station) : false
}

/** Which carried equipment id this rack station hands out, if any. */
export function equipmentForRack(content: Content, station: StationType): string | null {
  for (const [id, def] of Object.entries(content.recipes.equipment ?? {})) {
    if (def.rack === station) return id
  }
  return null
}

/** The board's chop, or whatever else a station transforms this ingredient into. */
export function transformFor(
  content: Content,
  station: StationType,
  from: IngredientId,
): TransformDef | undefined {
  return content.recipes.transforms.find((t) => t.station === station && t.from === from)
}

/**
 * What this equipment does to this ingredient *in this container*.
 *
 * All three arguments matter, and the shipped content proves it with the vessel
 * held constant: the same pan stews and fries on a stove and bakes in an oven,
 * and which of those happens is decided by which row exists. That is the design
 * doc's "static equipment holds pans" made into a lookup rather than a branch —
 * the container argument stays because content is free to reintroduce a second
 * kind tomorrow.
 */
export function cookableFor(
  content: Content,
  station: StationType,
  container: ContainerKind,
  from: IngredientId,
): CookableDef | undefined {
  return content.recipes.cookable.find(
    (c) => c.station === station && c.container === container && c.from === from,
  )
}

/** Whether this container on this equipment can do anything at all with an ingredient. */
export function canCook(
  content: Content,
  station: StationType,
  container: ContainerKind,
  from: IngredientId,
): boolean {
  return cookableFor(content, station, container, from) !== undefined
}

/** A duration from `timings.json`, addressed by the key a recipe named. */
export function durationTicks(content: Content, key: string): number {
  const ms = content.timings[key]
  if (typeof ms !== 'number' || !Number.isFinite(ms) || ms < 0) {
    throw new Error(`timings.json has no usable duration for "${key}"`)
  }
  return Math.max(1, msToTicks(ms))
}

export function chopTicks(content: Content, transform: TransformDef): number {
  return durationTicks(content, transform.durationKey)
}

export function washTicks(content: Content): number {
  return durationTicks(content, 'washMs')
}

export function plateReturnTicks(content: Content): number {
  return durationTicks(content, 'plateReturnDelayMs')
}

/**
 * How many plates the kitchen owns, and therefore how many either plate station
 * can hold.
 *
 * One number does both jobs on purpose: the clean stack starts full and the
 * dirty return has to be able to take every one of them back, so neither end of
 * the plate cycle can ever be the thing that blocks it. `platesPerMap` is not a
 * duration, so it is read here rather than through `durationTicks`.
 */
export function platesPerMap(content: Content): number {
  const value = content.timings.platesPerMap
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error('timings.json has no usable platesPerMap')
  }
  return Math.floor(value)
}

/** How long the extinguisher has to be held on a burning station. */
export function extinguishTicks(content: Content): number {
  return durationTicks(content, 'extinguishMs')
}

/**
 * The longest of the cook clocks the current contents ask for.
 *
 * The shipped pan holds one ingredient, so in practice this is that
 * ingredient's own clock and the bar at the stove is always about one thing.
 * The rule is written for any number anyway — a vessel with two things in it is
 * done when the slower of them is, and adding another restarts the phase from
 * now — because capacity is a recipe fact and content is free to ship a bigger
 * vessel tomorrow.
 */
export function phaseTicksFor(
  content: Content,
  station: StationType,
  container: Container,
  which: 'toDoneKey' | 'toBurntKey' | 'toFireKey',
  contents: IngredientId[] = container.contents,
): number | null {
  let longest: number | null = null
  for (const id of contents) {
    // After a phase completes the contents have already been transformed, so
    // the row is found by `to` as well as by `from`.
    const row =
      cookableFor(content, station, container.kind, id) ??
      content.recipes.cookable.find(
        (c) => c.station === station && c.container === container.kind && c.to === id,
      )
    if (!row) continue
    const key = which === 'toFireKey' ? (row.toFireKey ?? 'burntToFireMs') : row[which]
    const ticks = durationTicks(content, key)
    longest = longest === null ? ticks : Math.max(longest, ticks)
  }
  return longest
}

/** Fire is a property of the kitchen rather than of a recipe, so it has a floor. */
export function fireTicks(
  content: Content,
  station: StationType,
  container: Container,
): number {
  return phaseTicksFor(content, station, container, 'toFireKey') ?? durationTicks(content, 'burntToFireMs')
}
